import { createContext, useContext } from 'react';

/**
 * Theme name - simplified to only videogame
 */
export type ThemeName = 'videogame';

/**
 * Theme metadata for UI display
 */
export interface ThemeMetadata {
  name: ThemeName;
  displayName: string;
  description: string;
  icon: string;
}

/**
 * Available themes - simplified to only videogame
 */
export const THEMES: ThemeMetadata[] = [
  {
    name: 'videogame',
    displayName: 'VideoGame',
    description: '90s arcade style with pixel art, bright colors and thick borders',
    icon: '🎮',
  },
];

/**
 * Default theme - always videogame
 */
export const DEFAULT_THEME: ThemeName = 'videogame';

/**
 * Theme context value
 */
export interface ThemeContextValue {
  /** Current active theme (always videogame) */
  theme: ThemeName;

  /** Whether dark mode is enabled */
  isDarkMode: boolean;

  /** Toggle dark mode on/off */
  toggleDarkMode: () => void;

  /** Set dark mode explicitly */
  setDarkMode: (isDark: boolean) => void;

  /** Change the active theme (no-op, kept for API compatibility) */
  setTheme: (theme: ThemeName) => void;

  /** Available themes with metadata */
  themes: ThemeMetadata[];

  /** Get metadata for current theme */
  currentThemeMetadata: ThemeMetadata;
}

/**
 * Theme context
 */
export const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/**
 * Hook to access theme context
 * @throws Error if used outside ThemeProvider
 */
export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
};
